"use client";

import { ChevronDownIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { useEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import Link from "next/link";
import { motion } from "framer-motion";

import { IconMapping } from "@/components/Sidebar/IconMapping";
import NavDropdownPanel, {
  countSubs,
} from "@/components/nav/NavDropdownPanel";
import { useNavBar } from "@/components/nav/NavBarContext";
import { isNavItemActive } from "@/lib/navigation/is-nav-item-active";
import { openNavSearch } from "@/lib/navigation/nav-search-open";
import { cn } from "@/lib/utils";
import { EASE_PREMIUM, DURATION } from "@/lib/motion";

// One-hover dropdown — no side-flyouts. Anchor (left vs right) is measured
// against the trigger's viewport position just before opening, so the panel
// never clips the viewport edge regardless of where in the nav the trigger sits.
const MEGA_WIDTH = 720;
const COLUMN_WIDTH = 320; // matches w-80
const VIEWPORT_MARGIN = 16;

// Below three groups the grid columns sit nearly empty and the layout reads as
// unbalanced, so those dropdowns get a single tall column instead.
const MEGA_GRID_MIN_GROUPS = 3;

export default function NavDropdown({
  label,
  subtitle,
  items,
  isActive,
  currentHash,
  onNavigateHash,
  crossLink,
}) {
  const pathname = usePathname();
  // Only one nav dropdown is open at a time — shared across the whole bar so
  // hovering a sibling trigger instantly swaps panels instead of stacking them.
  const { isOpen, setIsOpen } = useNavBar(label);
  const [alignRight, setAlignRight] = useState(false);
  const closeTimeoutRef = useRef();
  const triggerRef = useRef();
  const containerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
    };
  }, []);

  const visibleItems = (items || []).filter((item) => item?.display);
  const useMegaGrid =
    visibleItems.filter((item) => countSubs(item) > 0).length >=
    MEGA_GRID_MIN_GROUPS;

  // Close on an outside click. The menu can hold keyboard focus, so it can't
  // rely on mouse-leave alone to dismiss.
  useEffect(() => {
    if (!isOpen) return;
    const onDocMouseDown = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [isOpen]);

  // Type-to-search: the first printable keystroke over an open menu hands off
  // to the global ⌘K dialog, seeded with that character, instead of filtering
  // this one menu. Someone typing at an open menu is naming a destination, and
  // the dialog searches every destination — including the ones this menu never
  // lists — so a per-menu filter was both narrower and a second search to
  // learn. Page links lead the results, since a menu is a list of pages.
  // Keys pressed inside another field, or with a modifier, pass through.
  useEffect(() => {
    if (!isOpen) return;
    const handleDocKeyDown = (event) => {
      const target = event.target;
      const tag = target?.tagName;
      const inFormField =
        tag === "INPUT" ||
        tag === "TEXTAREA" ||
        tag === "SELECT" ||
        target?.isContentEditable;
      if (inFormField) return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (event.key === "Escape") {
        setIsOpen(false);
        // Closing the panel unmounts whatever held focus, so hand it back to
        // the trigger instead of dropping the keyboard user on <body>.
        if (containerRef.current?.contains(document.activeElement)) {
          triggerRef.current?.focus();
        }
        return;
      }
      if (event.key.length === 1 && /\S/.test(event.key)) {
        event.preventDefault();
        setIsOpen(false);
        openNavSearch({ query: event.key, pagesFirst: true, menuLabel: label });
      }
    };
    document.addEventListener("keydown", handleDocKeyDown);
    return () => document.removeEventListener("keydown", handleDocKeyDown);
  }, [isOpen]);

  if (visibleItems.length === 0) return null;

  const handleMouseEnter = () => {
    if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);

    // Measure trigger's viewport position and decide anchor before opening,
    // so the panel never opens off-screen on the right.
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const panelWidth = useMegaGrid ? MEGA_WIDTH : COLUMN_WIDTH;
      const fitsLeftAnchored =
        rect.left + panelWidth + VIEWPORT_MARGIN <= window.innerWidth;
      setAlignRight(!fitsLeftAnchored);
    }

    setIsOpen(true);
  };

  // Keyboard equivalent of a hover. The panel only exists in the DOM while it
  // is open, so without this a keyboard user could focus the trigger and go no
  // further — every link in the menu would be pointer-only.
  const handleTriggerKeyDown = (event) => {
    if (event.key === "ArrowDown" && !isOpen) {
      event.preventDefault();
      handleMouseEnter();
    }
  };

  const handleMouseLeave = () => {
    // Keep the menu open while something inside it holds keyboard focus, so a
    // keyboard user isn't cut off by a stray mouse move. An outside click or
    // Escape still dismisses it.
    if (containerRef.current?.contains(document.activeElement)) return;
    closeTimeoutRef.current = setTimeout(() => setIsOpen(false), 200);
  };

  const renderIcon = (iconName) => {
    const Icon = IconMapping[iconName];
    return Icon ? <Icon className="w-4 h-4 shrink-0 text-gray-400" /> : null;
  };

  // Playbook section anchors expand on arrival via a custom event.
  const handleAnchorClick = (href) => {
    const hashIndex = (href || "").indexOf("#");
    onNavigateHash(hashIndex === -1 ? "" : href.substring(hashIndex + 1));
    if (hashIndex === -1) return;
    const sectionId = href.substring(hashIndex + 1);
    setTimeout(() => {
      window.dispatchEvent(
        new CustomEvent("openPlaybookSection", { detail: { sectionId } }),
      );
    }, 100);
  };

  const baseRowClass =
    "flex items-center gap-3 rounded-md px-2.5 py-2 text-sm transition-colors";
  const itemLinkClass = (active) =>
    `${baseRowClass} ${
      active
        ? "bg-brand-50 text-brand-800 font-medium"
        : "text-gray-700 hover:bg-brand-50 hover:text-brand-800"
    }`;

  const renderItemLink = (item) => {
    // An item with no href is a mis-flagged group — rendering it as a link
    // would emit <Link href={undefined}>, which throws in Next's formatUrl
    // and unmounts the whole app to global-error (the pre-manifest NavMenu
    // shipped exactly that crash for studios whose Performance group lost
    // every visible child).
    if (!item.href) return null;
    const active = isNavItemActive(pathname, item.href, currentHash);
    if (item.external) {
      return (
        <a
          href={item.href}
          target="_blank"
          rel="noopener noreferrer"
          className={itemLinkClass(active)}
        >
          {item.iconName && renderIcon(item.iconName)}
          <span className="truncate">{item.label}</span>
        </a>
      );
    }
    return (
      <Link
        href={item.href}
        className={itemLinkClass(active)}
        onClick={() => handleAnchorClick(item.href)}
      >
        {item.iconName && renderIcon(item.iconName)}
        <span className="truncate">{item.label}</span>
      </Link>
    );
  };

  const panelChrome =
    "absolute z-50 mt-2 rounded-xl border border-stone-200/80 bg-white shadow-[0_8px_28px_-12px_rgba(0,0,0,0.18)]";
  const panelAnchor = {
    right: alignRight ? 0 : "auto",
    left: alignRight ? "auto" : 0,
  };

  const panelMotion = {
    initial: { opacity: 0, y: -6, scale: 0.98 },
    animate: { opacity: 1, y: 0, scale: 1 },
    transition: { duration: DURATION.fast, ease: EASE_PREMIUM },
  };

  return (
    <div
      ref={containerRef}
      className="relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="true"
        aria-expanded={isOpen}
        onClick={() => (isOpen ? setIsOpen(false) : handleMouseEnter())}
        onKeyDown={handleTriggerKeyDown}
        className={`flex items-center gap-1 text-sm font-medium text-gray-700 transition-colors duration-150 hover:text-brand-800 ${
          isActive ? "text-brand-800" : ""
        }`}
      >
        {label}
        <ChevronDownIcon
          className={`w-3.5 h-3.5 transition-transform duration-150 ${isOpen ? "rotate-180" : ""}`}
        />
      </button>

      {isOpen && (
        <>
          {/* Hover bridge — keeps the dropdown open while cursor traverses the gap */}
          <div className="absolute left-0 right-0 top-full z-40 h-2" />

          <motion.div
            {...panelMotion}
            className={cn(panelChrome, !useMegaGrid && "w-80")}
            style={
              useMegaGrid
                ? {
                    ...panelAnchor,
                    width: `min(${MEGA_WIDTH}px, calc(100vw - 2rem))`,
                  }
                : panelAnchor
            }
          >
            {subtitle && (
              <div className="border-b border-gray-100 px-4.5 pb-2.5 pt-3 text-xs text-gray-500">
                {subtitle}
              </div>
            )}

            <NavDropdownPanel
              items={visibleItems}
              useMegaGrid={useMegaGrid}
              renderItemLink={renderItemLink}
            />

            {/* Wayfinding footer — rescues a user who opened the wrong menu
                (e.g. hunting for team reports inside their personal results)
                by pointing at the sibling menu's landing page. */}
            {crossLink && (
              <div className="border-t border-gray-100 p-2">
                <Link
                  href={crossLink.href}
                  onClick={() => setIsOpen(false)}
                  className="flex items-center justify-between gap-3 rounded-md px-2.5 py-2 text-xs text-gray-500 transition-colors hover:bg-brand-50 hover:text-brand-800"
                >
                  <span>{crossLink.prompt}</span>
                  <span className="flex shrink-0 items-center gap-0.5 font-semibold text-brand-800">
                    {crossLink.label}
                    <ChevronRightIcon className="h-3.5 w-3.5" />
                  </span>
                </Link>
              </div>
            )}
          </motion.div>
        </>
      )}
    </div>
  );
}
